import { useState, useEffect } from 'react';
import { isFeatureEnabled } from '@/config/featureFlags';

interface CachedContent {
  posts: any[];
  casts: any[];
  profiles: any[];
  timestamp: number;
  version: string;
}

const CACHE_KEY = 'yieldsocial_offline_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export const useOfflineCache = () => {
  const [isOnline, setIsOnline] = useState(true); // Default to true, will be set in useEffect
  const [cachedContent, setCachedContent] = useState<CachedContent | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);

  const isCacheEnabled = isFeatureEnabled('OFFLINE_CACHE');

  useEffect(() => {
    // Set initial online status safely
    setIsOnline(navigator.onLine);
    
    if (!isCacheEnabled) return;

    // Load cached content on mount
    const loadCache = () => {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const content = JSON.parse(cached);
          // Check if cache is still valid
          if (Date.now() - content.timestamp < CACHE_DURATION) {
            setCachedContent(content);
            setLastSyncTime(content.timestamp);
          } else {
            // Clear expired cache
            localStorage.removeItem(CACHE_KEY);
          }
        }
      } catch (error) {
        console.warn('Failed to load offline cache:', error);
        localStorage.removeItem(CACHE_KEY);
      }
    };

    loadCache();

    // Monitor online/offline status
    const handleOnline = () => {
      setIsOnline(true);
      console.log('Connection restored');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('Connection lost - using cached content');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isCacheEnabled]);

  const cacheContent = (posts: any[], casts: any[], profiles: any[]) => {
    if (!isCacheEnabled) return;

    try {
      const content: CachedContent = {
        posts: posts.slice(0, 20), // Cache only first 20 posts
        casts: casts.slice(0, 10), // Cache only first 10 casts
        profiles: profiles.slice(0, 50), // Cache profile info
        timestamp: Date.now(),
        version: '1.5.0'
      };

      localStorage.setItem(CACHE_KEY, JSON.stringify(content));
      setCachedContent(content);
      setLastSyncTime(content.timestamp);
      
      console.log('Content cached for offline viewing');
    } catch (error) {
      console.warn('Failed to cache content:', error);
      // If storage is full, try to clear old cache and retry
      if (error instanceof DOMException && error.code === 22) {
        localStorage.removeItem(CACHE_KEY);
        console.log('Storage full - cleared old cache');
      }
    }
  };

  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    setCachedContent(null);
    setLastSyncTime(null);
  };

  const getCacheStatus = () => {
    if (!isCacheEnabled) return { enabled: false };
    
    return {
      enabled: true,
      hasCache: !!cachedContent,
      isOnline,
      lastSync: lastSyncTime,
      cacheAge: lastSyncTime ? Date.now() - lastSyncTime : null,
      itemsCached: cachedContent ? {
        posts: cachedContent.posts.length,
        casts: cachedContent.casts.length,
        profiles: cachedContent.profiles.length
      } : null
    };
  };

  return {
    isOnline,
    cachedContent,
    cacheContent,
    clearCache,
    getCacheStatus,
    isCacheEnabled
  };
};